import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { sendWhatsApp } from '../whatsappClient'
import { dayOfWeekIST } from '../timeHelpers'
import { getTeacherAvailability, getTeacherConflictsInRange, isWithinAvailability, hasConflictOnDate, DEFAULT_DURATION_MINUTES } from '../availabilityHelper'

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday:'short', day:'numeric', month:'short', year:'numeric'
  })
}
function fmtTime(t) {
  if (!t) return '—'
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}
function shiftToWeekday(dateStr, targetDay) {
  const d = new Date(dateStr + 'T00:00:00')
  const delta = (targetDay - d.getDay() + 7) % 7
  d.setDate(d.getDate() + delta)
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
}

export default function BulkReschedule() {
  const [students,   setStudents]   = useState([])
  const [selStudent, setSelStudent] = useState('')
  const [whichCls,   setWhichCls]   = useState('future') // future | all
  const [fromDay,    setFromDay]    = useState(null) // null = any day
  const [fromTime,   setFromTime]   = useState('')   // '' = any time
  const [newDay,     setNewDay]     = useState(1)
  const [newTime,    setNewTime]    = useState('')
  const [sendMail,   setSendMail]   = useState(true)
  const [reason,     setReason]     = useState('')
  const [preview,    setPreview]    = useState([])
  const [loadingPrev,setLoadingPrev]= useState(false)
  const [busy,       setBusy]       = useState(false)
  const [done,       setDone]       = useState(null)
  const [err,        setErr]        = useState('')
  const [step,       setStep]       = useState(1)

  useEffect(() => { loadStudents() }, [])

  async function loadStudents() {
    const { data } = await supabase.from('profiles').select('id,full_name,email,phone,timezone').eq('role','student').order('full_name')
    setStudents(data || [])
  }

  async function loadPreview() {
    setErr('')
    if (!selStudent) return setErr('Select a student.')

    setLoadingPrev(true)
    const today = new Date().toISOString().slice(0,10)

    try {
      const { data: enrols } = await supabase.from('enrollments').select('class_id').eq('student_id', selStudent)
      const classIds = (enrols || []).map(e => e.class_id).filter(Boolean)
      if (!classIds.length) {
        setPreview([]); setLoadingPrev(false); setStep(2)
        setErr('This student has no enrollments found.')
        return
      }

      let q = supabase.from('classes').select('*').in('id', classIds)
      if (whichCls === 'future') q = q.gte('class_date', today)
      q = q.order('class_date').order('start_time')

      const { data: cls, error: clsErr } = await q
      if (clsErr) throw new Error(clsErr.message)

      let result = (cls || [])
        .filter(c => !c.is_cancelled)
        .filter(c => fromDay === null || new Date(c.class_date + 'T00:00:00').getDay() === fromDay)
        .filter(c => !fromTime || (c.start_time || '').slice(0,5) === fromTime)
        .map(c => ({
          ...c,
          new_date: shiftToWeekday(c.class_date, newDay),
          new_time: newTime || c.start_time,
        }))

      // Availability check — one fetch per distinct teacher involved (not
      // per class), covering the whole date range being moved into.
      const teacherIds = [...new Set(result.map(c => c.teacher_id).filter(Boolean))]
      if (teacherIds.length) {
        const newDates = result.map(c => c.new_date)
        const rangeFrom = newDates.reduce((a, b) => a < b ? a : b)
        const rangeTo   = newDates.reduce((a, b) => a > b ? a : b)
        const availByTeacher = {}
        const conflictsByTeacher = {}
        for (const tid of teacherIds) {
          availByTeacher[tid]     = await getTeacherAvailability(tid)
          conflictsByTeacher[tid] = await getTeacherConflictsInRange({ teacherId: tid, fromDate: rangeFrom, toDate: rangeTo })
        }
        result = result.map(c => {
          if (!c.teacher_id || !c.new_time) return { ...c, availIssue: null }
          const dur = c.duration_minutes || DEFAULT_DURATION_MINUTES
          const withinHours = isWithinAvailability(availByTeacher[c.teacher_id], dayOfWeekIST(c.new_date), c.new_time, dur)
          const otherClasses = (conflictsByTeacher[c.teacher_id] || []).filter(x => x.id !== c.id)
          const hasConflict  = hasConflictOnDate(otherClasses, c.new_date, c.new_time, dur)
          return { ...c, availIssue: hasConflict ? 'conflict' : !withinHours ? 'outside' : null }
        })
      }

      setPreview(result)
      setErr(result.length === 0 ? 'No classes matched — check the student and the "only move classes on" filter.' : '')
    } catch(e) {
      setErr('Error loading classes: ' + e.message)
    }
    setLoadingPrev(false)
    setStep(2)
  }

  async function bulkReschedule() {
    if (!preview.length) return
    setErr('')

    const conflictCount = preview.filter(c => c.availIssue === 'conflict').length
    const outsideCount  = preview.filter(c => c.availIssue === 'outside').length
    if (conflictCount > 0) {
      return setErr(`⛔ ${conflictCount} of ${preview.length} classes conflict with the teacher's existing schedule at the new time — fix or exclude those before proceeding.`)
    }
    if (outsideCount > 0 && !window.confirm(`⚠ ${outsideCount} of ${preview.length} classes would fall outside the teacher's declared working hours at the new time. Reschedule anyway?`)) {
      return
    }

    setBusy(true)

    const student = students.find(s => s.id === selStudent)

    try {
      for (const c of preview) {
        const { error } = await supabase.from('classes').update({
          class_date:            c.new_date,
          start_time:            c.new_time || null,
          rescheduled_from_date: c.class_date,
          rescheduled_from_time: c.start_time || null,
        }).eq('id', c.id)
        if (error) throw new Error(error.message)

        const d = new Date(c.new_date + 'T00:00:00')
        await supabase.from('events').update({
          day:   d.getDate(), month: d.getMonth()+1, year: d.getFullYear(),
          time:  c.new_time || null,
        }).eq('class_id', c.id)
      }

      if (sendMail && student?.email) {
        await sendEmail('class_scheduled', student.email, {
          name:        student.full_name,
          classTitle:  `${preview.length} classes moved to ${DAY_NAMES[newDay]}${newTime ? ` at ${fmtTime(newTime)}` : ''}`,
          classDate:   `${fmtDate(preview[0]?.new_date)} onward`,
          startTime:   newTime || null,
          teacherName: null,
          zoomLink:    null,
          studentTZ:   student.timezone,
        })
      }
      if (sendMail && student?.phone) {
        // class_rescheduled_wa is POSITIONAL — order: name, class_name, old_date_time, new_date_time.
        sendWhatsApp('class_rescheduled_wa', student.phone, [
          student.full_name,
          `${preview.length} classes`,
          'multiple dates',
          `${DAY_NAMES[newDay]}${newTime ? ` at ${fmtTime(newTime)}` : ''} (from ${fmtDate(preview[0]?.new_date)})`,
        ])
      }

      setDone({ count: preview.length, studentName: student?.full_name, emailSent: sendMail && !!student?.email })
      setStep(3)
    } catch(e) {
      setErr('Reschedule failed: ' + e.message)
    }
    setBusy(false)
  }

  function reset() {
    setStep(1); setPreview([]); setDone(null); setErr('')
    setSelStudent(''); setReason(''); setNewTime('')
    setFromDay(null); setFromTime('')
  }

  const student = students.find(s => s.id === selStudent)

  const inp = {
    width:'100%', background:'rgba(var(--fg-rgb),0.06)',
    border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px',
    padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))',
    outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box',
  }
  const lbl = {
    display:'block', fontSize:'10px', fontWeight:700,
    color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em',
    textTransform:'uppercase', marginBottom:'5px', marginTop:'12px',
  }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>↺ Bulk Reschedule Student Classes</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'20px' }}>Move all of one student's classes to a new day of the week (and optionally a new time) in one go.</div>

      {/* ── STEP 1: Filters ── */}
      {step === 1 && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
          <div>
            <label style={{ ...lbl, marginTop:0 }}>Select Student *</label>
            <select style={{ ...inp, colorScheme:'dark' }} value={selStudent} onChange={e => setSelStudent(e.target.value)}>
              <option value="">— Choose student —</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>

            <label style={lbl}>Which Classes</label>
            <div style={{ display:'grid', gap:'6px' }}>
              {[
                { v:'future', label:'Future classes only',         desc:'Only upcoming classes from today' },
                { v:'all',    label:'All classes (past + future)', desc:'Complete history'                 },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setWhichCls(o.v)}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'10px', border:`1px solid ${whichCls===o.v?'#1e90ff':'rgba(var(--fg-rgb),0.07)'}`, background:whichCls===o.v?'rgba(30,144,255,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', width:'100%' }}>
                  <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:`2px solid ${whichCls===o.v?'#1e90ff':'rgba(var(--fg-rgb),var(--a20))'}`, background:whichCls===o.v?'#1e90ff':'transparent', flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:700, color:whichCls===o.v?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a60))' }}>{o.label}</div>
                    <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{o.desc}</div>
                  </div>
                </button>
              ))}
            </div>

            <label style={lbl}>Only Move Classes Currently On (optional)</label>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
              <button type="button" onClick={()=>setFromDay(null)} style={{ padding:'8px 12px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:700, background:fromDay===null?'#1e90ff':'rgba(var(--fg-rgb),0.05)', color:fromDay===null?'#fff':'rgba(var(--fg-rgb),var(--a50))', fontFamily:'inherit' }}>Any day</button>
              {DAY_NAMES.map((d,i)=>(
                <button key={i} type="button" onClick={()=>setFromDay(i)} style={{ padding:'8px 12px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:700, background:fromDay===i?'#1e90ff':'rgba(var(--fg-rgb),0.05)', color:fromDay===i?'#fff':'rgba(var(--fg-rgb),var(--a50))', fontFamily:'inherit' }}>{d.slice(0,3)}</button>
              ))}
            </div>
            <input style={inp} type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} placeholder="Any time" />
            <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'4px' }}>Narrows which classes are affected — e.g. only Friday 3:00 AM classes. Leave both as "Any" to move everything.</div>

            <label style={lbl}>New Day of Week *</label>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {DAY_NAMES.map((d,i)=>(
                <button key={i} type="button" onClick={()=>setNewDay(i)} style={{ padding:'8px 12px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:700, background:newDay===i?'#1e90ff':'rgba(var(--fg-rgb),0.05)', color:newDay===i?'#fff':'rgba(var(--fg-rgb),var(--a50))', fontFamily:'inherit' }}>{d.slice(0,3)}</button>
              ))}
            </div>

            <label style={lbl}>New Time (optional)</label>
            <input style={inp} type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
            <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'4px' }}>Leave blank to keep each class's original time.</div>

            <label style={lbl}>Reason (optional)</label>
            <input style={inp} type="text" placeholder="e.g. Student requested new schedule..." value={reason} onChange={e => setReason(e.target.value)}/>

            {selStudent && (
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'12px', padding:'10px 12px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px' }}>
                <button type="button" onClick={() => setSendMail(!sendMail)}
                  style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', position:'relative', background:sendMail?'#1e90ff':'rgba(var(--fg-rgb),0.1)', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:'2px', left:sendMail?'18px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                </button>
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))' }}>Send notification email to student</div>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{student?.email || 'No email on file'}</div>
                </div>
              </div>
            )}

            {err && <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}

            <button onClick={loadPreview} disabled={loadingPrev}
              style={{ marginTop:'16px', width:'100%', padding:'12px', background:'linear-gradient(135deg,#8b5cf6,#6d28d9)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:loadingPrev?'not-allowed':'pointer', fontFamily:'inherit' }}>
              {loadingPrev ? '⏳ Loading classes...' : 'Preview Changes →'}
            </button>
          </div>

          <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
            <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'14px', letterSpacing:'0.08em' }}>SELECTED FILTERS</div>
            {[
              { label:'Student',  value: student?.full_name || '—' },
              { label:'Classes',  value: whichCls==='future'?'Future only':'All classes' },
              { label:'Only from',value: `${fromDay===null?'Any day':DAY_NAMES[fromDay]}${fromTime?` · ${fmtTime(fromTime)}`:''}` },
              { label:'New Day',  value: DAY_NAMES[newDay] },
              { label:'New Time', value: newTime ? fmtTime(newTime) : 'Keep original per class' },
              { label:'Email',    value: sendMail?'Yes — send email':'No — silent' },
            ].map(r => (
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'8px', marginBottom:'4px' }}>
                <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))' }}>{r.label}</span>
                <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a80))', fontWeight:500 }}>{r.value}</span>
              </div>
            ))}
            <div style={{ marginTop:'16px', padding:'12px', background:'rgba(139,92,246,0.07)', border:'0.5px solid rgba(139,92,246,0.2)', borderRadius:'10px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a45))', lineHeight:1.65 }}>
              💡 Each class moves forward within its own week to land on the new day (e.g. Tue → Thu shifts it 2 days later). The weekly cadence between classes is preserved.
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 2: Preview & Confirm ── */}
      {step === 2 && (
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
            <div>
              <div style={{ fontSize:'15px', fontWeight:800, color:'#a78bfa' }}>↺ {preview.length} classes will be rescheduled</div>
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'3px' }}>
                {student?.full_name && `Student: ${student.full_name}`} · Moving to {DAY_NAMES[newDay]}{newTime ? ` at ${fmtTime(newTime)}` : ''}
              </div>
            </div>
            <button onClick={() => setStep(1)}
              style={{ fontSize:'12px', padding:'6px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
              ← Change filters
            </button>
          </div>

          {preview.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a25))', fontSize:'13px' }}>
              <div style={{ fontSize:'36px', marginBottom:'12px' }}>✅</div>
              No classes match the selected filters.
            </div>
          ) : (
            <>
              <div style={{ maxHeight:'380px', overflowY:'auto', display:'grid', gap:'4px', marginBottom:'16px' }}>
                {preview.map((cls, i) => (
                  <div key={cls.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', background: cls.availIssue==='conflict' ? 'rgba(239,68,68,0.08)' : cls.availIssue==='outside' ? 'rgba(244,163,53,0.08)' : 'rgba(139,92,246,0.06)', borderRadius:'9px', border:`0.5px solid ${cls.availIssue==='conflict' ? 'rgba(239,68,68,0.25)' : cls.availIssue==='outside' ? 'rgba(244,163,53,0.25)' : 'rgba(139,92,246,0.15)'}` }}>
                    <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'5px', background:'rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a40))', flexShrink:0 }}>#{i+1}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cls.title}</div>
                      <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'1px', display:'flex', gap:'6px', alignItems:'center' }}>
                        <span style={{ textDecoration:'line-through' }}>{fmtDate(cls.class_date)}{cls.start_time && ` · ${fmtTime(cls.start_time)}`}</span>
                        <span style={{ color:'#a78bfa' }}>→</span>
                        <span style={{ color:'#34d399', fontWeight:600 }}>{fmtDate(cls.new_date)}{cls.new_time && ` · ${fmtTime(cls.new_time)}`}</span>
                      </div>
                    </div>
                    {cls.availIssue === 'conflict' && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(239,68,68,0.15)', color:'#f87171', flexShrink:0 }}>⛔ Conflict</span>}
                    {cls.availIssue === 'outside'  && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(244,163,53,0.15)', color:'#f4a335', flexShrink:0 }}>⚠ Outside hours</span>}
                  </div>
                ))}
              </div>

              <div style={{ padding:'12px 14px', background:'rgba(139,92,246,0.08)', border:'0.5px solid rgba(139,92,246,0.25)', borderRadius:'10px', marginBottom:'14px', fontSize:'12px', color:'#a78bfa' }}>
                ↺ This will move all {preview.length} classes to their new date/time shown above.
                {sendMail && student?.email && <span> A notification email will be sent to <strong>{student.email}</strong>.</span>}
              </div>

              {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px', marginBottom:'10px' }}>{err}</div>}

              <button onClick={bulkReschedule} disabled={busy}
                style={{ width:'100%', padding:'13px', background:busy?'rgba(100,100,100,0.3)':'linear-gradient(135deg,#8b5cf6,#6d28d9)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit' }}>
                {busy ? '⏳ Processing...' : `↺ Reschedule All ${preview.length} Classes`}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── STEP 3: Done ── */}
      {step === 3 && done && (
        <div style={{ textAlign:'center', padding:'3rem', background:'rgba(139,92,246,0.06)', border:'0.5px solid rgba(139,92,246,0.2)', borderRadius:'16px' }}>
          <div style={{ fontSize:'52px', marginBottom:'14px' }}>↺</div>
          <div style={{ fontSize:'18px', fontWeight:800, color:'#a78bfa', marginBottom:'8px' }}>Bulk Reschedule Complete!</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', maxWidth:'320px', margin:'0 auto 20px' }}>
            <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'14px' }}>
              <div style={{ fontSize:'28px', fontWeight:800, color:'#a78bfa' }}>{done.count}</div>
              <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px' }}>Classes rescheduled</div>
            </div>
            <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'14px' }}>
              <div style={{ fontSize:'28px', fontWeight:800, color:done.emailSent?'#34d399':'#94a3b8' }}>{done.emailSent?'✓':'—'}</div>
              <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px' }}>{done.emailSent?'Email sent':'No email'}</div>
            </div>
          </div>
          {done.studentName && <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))', marginBottom:'20px' }}>Student: {done.studentName}</div>}
          <button onClick={reset}
            style={{ padding:'11px 28px', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'13px', fontWeight:700, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
            Reschedule Another Student
          </button>
        </div>
      )}
    </div>
  )
}
